import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, TrendingUp, CheckCircle, Award, Briefcase, Star, Users, FileText, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnimatedCounter from '../components/AnimatedCounter';import { reviews } from '../data/reviews';

const WHATSAPP_URL = `https://wa.me/14377570478?text=${encodeURIComponent(
  'Hi VK Tax Guide! I would like to know more about your tax filing services.'
)}`;

const stats = [
  { icon: FileText, end: 2500, suffix: '+', label: 'Returns Filed' },
  { icon: Users, end: 800, suffix: '+', label: 'Clients Served' },
  { icon: Clock, end: 3, suffix: '+', label: 'Years of Experience' },
  { icon: MapPin, end: 10, suffix: '+', label: 'Provinces Served' },
];

const Home = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero Section with background image */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background image + overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/8296970/pexels-photo-8296970.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt="Professional accounting and tax services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#003a52]/95 via-[#005782]/90 to-[#01435c]/85" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">
              Your Trusted Tax Partner in Canada
            </h1>
            <p className="text-xl md:text-2xl leading-relaxed text-white/90 mb-4">
              Guiding Canadians through complex taxes with total clarity!
            </p>
            <p className="text-lg text-yellow-300 font-semibold mb-8">
              Right filings. Zero stress.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                data-testid="hero-whatsapp-btn"
                className="bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-lg px-8 py-4 text-lg font-medium transition-all shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center gap-2"
              >
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  Chat on WhatsApp
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <Link to="/services">
                <Button
                  data-testid="hero-learn-more-btn"
                  className="bg-white text-[#005782] hover:bg-slate-100 rounded-lg px-8 py-4 text-lg font-medium w-full sm:w-auto"
                >
                  Our Services
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Rating summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-14"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-2xl font-bold text-white">5.0</span>
              <span className="text-white/80">based on 50+ Google reviews</span>
            </div>
            <p className="text-white/80 mt-2">
              Trusted by hundreds of Canadians for their tax and accounting needs
            </p>
          </motion.div>
        </div>

        {/* Reviews horizontal auto-scroll marquee */}
        <div className="review-marquee-wrapper overflow-hidden mt-8 relative z-10">
          <div className="animate-review-marquee flex gap-4 px-6 md:px-12">
            {[...reviews, ...reviews].map((review, index) => (
              <a
                key={index}
                href={review.reviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`hero-review-${index}`}
                className="w-80 flex-shrink-0 bg-slate-900/70 backdrop-blur-md rounded-2xl p-5 border border-white/10 hover:border-white/40 transition-all block no-underline"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${review.color} flex items-center justify-center text-white font-bold`}>
                      {review.initials}
                    </div>
                    <span className="font-semibold text-white text-sm">{review.name}</span>
                  </div>
                  <img
                    src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png"
                    alt="Google"
                    className="w-5 h-5"
                  />
                </div>
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-white/80 text-sm leading-relaxed line-clamp-4">
                  {review.content}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section (animated) */}
      <section className="py-20 bg-[#005782] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                  data-testid={`home-stat-${index}`}
                >
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-yellow-300" />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    <AnimatedCounter end={stat.end} suffix={stat.suffix} />
                  </div>
                  <div className="text-white/80 font-medium">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
          <p className="text-center text-white/70 mt-10 text-lg">
            Proudly serving clients across all provinces in Canada
          </p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4">
              Why Choose VK Tax Guide?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Professional tax services designed for Canadian individuals and families
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-16 h-16 bg-[#E0F2FE] rounded-xl flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-[#005782]" />
              </div>
              <h3 className="text-2xl font-semibold text-[#0F172A] mb-3">
                Expert Guidance
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Our certified tax professionals stay updated with the latest CRA regulations to maximize your returns.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-16 h-16 bg-[#E0F2FE] rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-[#005782]" />
              </div>
              <h3 className="text-2xl font-semibold text-[#0F172A] mb-3">
                100% Secure
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Your personal and financial information is protected with bank-level encryption and security.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-16 h-16 bg-[#E0F2FE] rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-[#005782]" />
              </div>
              <h3 className="text-2xl font-semibold text-[#0F172A] mb-3">
                Maximum Returns
              </h3>
              <p className="text-slate-600 leading-relaxed">
                We identify every deduction and credit you're entitled to, ensuring you get the best possible refund.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#005782] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to File Your Taxes with Confidence?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get started today and experience stress-free tax filing with VK Tax Guide
          </p>
          <Button
            asChild
            data-testid="home-cta-whatsapp-btn"
            className="bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-lg px-8 py-4 text-lg font-medium shadow-lg inline-flex items-center gap-2"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Chat with us on WhatsApp
              <ArrowRight className="w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>

      {/* Outsourcing Highlight Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 via-white to-[#E0F2FE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full mb-6 border border-yellow-200">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm font-bold uppercase tracking-wider">
                  For CPA and Accounting Firms
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
                Outsourcing &<br />
                <span className="text-[#005782]">Manpower Services</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Scale your firm with our experienced offshore accounting professionals. Reduce costs by up to <span className="font-bold text-[#005782]">60%</span> while maintaining quality Canadian tax expertise.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-lg">Tax preparation support (T1, T2, GST/HST)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-lg">Full-cycle bookkeeping & accounting</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-lg">Compilation & assurance support</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 text-lg">Data security & confidentiality guaranteed</span>
                </li>
              </ul>
              <Link to="/cpa-services">
                <Button
                  data-testid="home-cpa-cta-btn"
                  className="bg-[#005782] text-white hover:bg-[#01435c] rounded-lg px-8 py-4 text-lg font-medium shadow-lg hover:shadow-xl active:scale-95 flex items-center gap-2"
                >
                  Learn About Outsourcing Services
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4 auto-rows-fr">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 flex flex-col justify-center"
              >
                <div className="text-4xl font-bold text-[#005782] mb-2">60%</div>
                <div className="text-slate-600 font-medium">Cost Savings Guaranteed</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-[#005782] text-white rounded-2xl p-6 shadow-lg flex flex-col justify-center"
              >
                <div className="text-4xl font-bold text-yellow-300 mb-2">24/7</div>
                <div className="text-white/90 font-medium">Support Coverage</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-[#005782] text-white rounded-2xl p-6 shadow-lg flex flex-col justify-center"
              >
                <div className="text-3xl font-bold text-yellow-300 mb-2">Experienced</div>
                <div className="text-white/90 font-medium">Accountants</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 flex flex-col justify-center"
              >
                <div className="text-4xl font-bold text-[#005782] mb-2">100%</div>
                <div className="text-slate-600 font-medium">Secure &amp; Confidential</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
